/**
 * Czysta logika układu stajni (WP9) — bez Phaser.
 * Niezależna od home (locationType='stable', §16).
 */
import {
  BLOCKS,
  GRID_COLS,
  GRID_ROWS,
  MAX_BLOCKS,
  type BlockDef,
  type PlacedBlock,
  validateLayout,
} from '@pieskowo/shared';

export const LOCAL_STABLE_LAYOUT_KEY = 'pieskowo.stableLayout';
export const LOCAL_STABLE_VERSION_KEY = 'pieskowo.stableLayoutVersion';
/** Wspólny zapas klocków z domem (ten sam klucz lustra offline). */
export const LOCAL_OWNED_BLOCKS_KEY = 'pieskowo.ownedBlocks';

/** Bloki stajenne z katalogu shared. */
export function stableBlockCatalog(): BlockDef[] {
  return BLOCKS.filter((b) => b.category === 'stable');
}

/** Offline: zapas klocków stajennych (gdy brak owned z serwera). */
export function defaultOwnedStableBlocks(qtyPerId = 8): string[] {
  const out: string[] = [];
  for (const b of stableBlockCatalog()) {
    for (let i = 0; i < qtyPerId; i++) out.push(b.id);
  }
  return out;
}

export function normalizeLayout(raw: unknown): PlacedBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: PlacedBlock[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.id !== 'string') continue;
    if (!Number.isInteger(o.x) || !Number.isInteger(o.y) || !Number.isInteger(o.layer)) {
      continue;
    }
    const x = o.x as number;
    const y = o.y as number;
    const layer = o.layer as number;
    if (x < 0 || x >= GRID_COLS || y < 0 || y >= GRID_ROWS) continue;
    out.push({ id: o.id, x, y, layer });
  }
  return out;
}

export function findAt(
  layout: readonly PlacedBlock[],
  x: number,
  y: number,
  layer?: number,
): PlacedBlock | undefined {
  if (layer === undefined) {
    const at = layout.filter((b) => b.x === x && b.y === y);
    if (at.length === 0) return undefined;
    return at.reduce((a, b) => (b.layer >= a.layer ? b : a));
  }
  return layout.find((b) => b.x === x && b.y === y && b.layer === layer);
}

export function countUsed(layout: readonly PlacedBlock[], blockId: string): number {
  return layout.reduce((n, b) => n + (b.id === blockId ? 1 : 0), 0);
}

export function countOwned(owned: readonly string[], blockId: string): number {
  return owned.reduce((n, id) => n + (id === blockId ? 1 : 0), 0);
}

export function remainingOwned(
  owned: readonly string[],
  layout: readonly PlacedBlock[],
  blockId: string,
): number {
  return Math.max(0, countOwned(owned, blockId) - countUsed(layout, blockId));
}

export type PlaceResult =
  | { ok: true; layout: PlacedBlock[] }
  | { ok: false; reason: string };

export function placeBlock(
  layout: readonly PlacedBlock[],
  block: PlacedBlock,
  owned: readonly string[],
): PlaceResult {
  if (layout.length >= MAX_BLOCKS) return { ok: false, reason: 'too_many' };
  if (block.x < 0 || block.x >= GRID_COLS || block.y < 0 || block.y >= GRID_ROWS) {
    return { ok: false, reason: 'oob' };
  }
  if (findAt(layout, block.x, block.y, block.layer)) {
    return { ok: false, reason: 'overlap' };
  }
  if (remainingOwned(owned, layout, block.id) < 1) {
    return { ok: false, reason: 'qty' };
  }
  const next = [...layout, { ...block }];
  const v = validateLayout(next, owned as string[]);
  if (!v.ok) return { ok: false, reason: v.reason };
  return { ok: true, layout: next };
}

export function removeBlockAt(
  layout: readonly PlacedBlock[],
  x: number,
  y: number,
  layer?: number,
): PlacedBlock[] {
  if (layer === undefined) {
    const top = findAt(layout, x, y);
    if (!top) return [...layout];
    return layout.filter(
      (b) => !(b.x === x && b.y === y && b.layer === top.layer),
    );
  }
  return layout.filter((b) => !(b.x === x && b.y === y && b.layer === layer));
}

export function defaultLayerForBlock(blockId: string): number {
  const def = BLOCKS.find((b) => b.id === blockId);
  if (!def) return 1;
  if (def.category === 'floor' || def.category === 'wall' || def.category === 'roof') {
    return 0;
  }
  return 1;
}

export function blockColor(id: string): number {
  switch (id) {
    case 'fence_wood':
      return 0xc4a574;
    case 'hay_bale':
      return 0xf0e68c;
    case 'horseshoe_deco':
      return 0x90a4ae;
    case 'trough':
      return 0xa1887f;
    default: {
      const def = BLOCKS.find((b) => b.id === id);
      if (def?.category === 'stable') return 0xa5d6a7;
      return 0xbcaaa4;
    }
  }
}

export function blockLabelPl(id: string): string {
  return BLOCKS.find((b) => b.id === id)?.namePl ?? id;
}

/** Rodzaj ikony narzędzia/klocka stajennego (bez emoji — proceduralna tekstura). */
export type StableBlockIconKind =
  | 'plank'
  | 'hay'
  | 'horseshoe'
  | 'bucket'
  | 'horse';

/** Mapowanie id klocka → rodzaj ikony (deska, siano, podkowa, wiadro, koń). */
export function blockIconKind(id: string): StableBlockIconKind {
  switch (id) {
    case 'fence_wood':
      return 'plank';
    case 'hay_bale':
      return 'hay';
    case 'horseshoe_deco':
      return 'horseshoe';
    case 'trough':
      return 'bucket';
    default:
      return 'horse';
  }
}

/** Klucz tekstury Phaser dla ikony klocka stajennego. */
export function blockTextureKey(id: string): string {
  return `stable_block_${blockIconKind(id)}`;
}
